/**
 * Variant Description Switcher
 * ============================
 * Dynamically updates product description when variant changes.
 * 
 * TWO MODES:
 * 1. JSON Mode (faster): Loads all descriptions upfront from alternate template
 * 2. Section API Mode (fallback): Fetches on-demand via Shopify Section Rendering
 * 
 * Usage:
 * 1. Create variant metafield: custom.variant_description
 * 2. Add this script to your theme
 * 3. Include in theme.liquid: <script src="{{ 'variant-description.js' | asset_url }}" defer></script>
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // Adjust these selectors if needed for your theme
  // ============================================
  const CONFIG = {
    // CSS selector for the description container to update.
    // Fashionopolism uses [data-product-description] on the wrapper div;
    // additional selectors are kept as fallbacks for other product page layouts.
    descriptionSelector: [
      '[data-product-description]',
      '.product__decription-container',
      '.main-product-description-product-template',
      '.product-description',
      '.product__description',
      '.rte-page'
    ].join(', '),

    // The section ID (sections/variant-description-ajax.liquid)
    sectionId: 'variant-description-ajax',

    // Animation duration in milliseconds
    animationDuration: 200,

    // Enable console logging for debugging
    debug: false,

    // JSON mode requires a product.variant-descriptions.liquid alternate template.
    // Fashionopolism does not have one, so we use Section API only.
    useJsonMode: false
  };

  // ============================================
  // DATA STORAGE
  // ============================================
  let variantData = null;        // Stores loaded JSON data
  const cache = new Map();       // Cache for Section API fallback
  let defaultDescription = null; // Store the original description

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  
  function log(...args) {
    if (CONFIG.debug) console.log('[VariantDesc]', ...args);
  }

  function getProductHandle() {
    const path = window.location.pathname;
    const match = path.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function getProductUrl() {
    const path = window.location.pathname;
    if (path.includes('/products/')) {
      return path.split('?')[0];
    }
    return null;
  }

  function getCurrentVariantId() {
    const select = document.querySelector('select[name="id"]');
    if (select && select.value) return select.value;
    
    const checkedInput = document.querySelector('input[name="id"]:checked');
    if (checkedInput) return checkedInput.value;
    
    const hiddenInput = document.querySelector('input[name="id"][type="hidden"]');
    if (hiddenInput) return hiddenInput.value;
    
    const params = new URLSearchParams(window.location.search);
    return params.get('variant');
  }

  function getDescriptionContainer() {
    return document.querySelector(CONFIG.descriptionSelector);
  }

  // ============================================
  // JSON MODE - Load all descriptions at once
  // ============================================
  
  async function loadVariantDescriptionsJson() {
    const productHandle = getProductHandle();
    if (!productHandle) return null;

    try {
      // Load from the alternate template view
      const url = `/products/${productHandle}?view=variant-descriptions`;
      
      log('Loading variant descriptions JSON from:', url);
      
      const response = await fetch(url, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      log('Loaded variant data:', data);
      
      return data;
    } catch (error) {
      log('JSON mode failed, will use Section API fallback:', error);
      return null;
    }
  }

  function getDescriptionFromJson(variantId) {
    if (!variantData || !variantData.variants) return null;
    
    const variant = variantData.variants[variantId];
    if (variant && variant.description) {
      return variant.description;
    }
    
    // Return default product description as fallback
    return variantData.default_description || null;
  }

  // ============================================
  // SECTION API MODE - Fetch on demand (fallback)
  // ============================================
  
  async function fetchVariantDescriptionFromSection(variantId) {
    const productUrl = getProductUrl();
    if (!productUrl || !variantId) return null;

    const cacheKey = `${productUrl}-${variantId}`;
    
    if (cache.has(cacheKey)) {
      log('Cache HIT for variant', variantId);
      return cache.get(cacheKey);
    }

    log('Fetching from Section API for variant', variantId);

    try {
      const url = `${productUrl}?variant=${variantId}&section_id=${CONFIG.sectionId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      cache.set(cacheKey, html);
      
      return html;
    } catch (error) {
      console.error('[VariantDesc] Section API error:', error);
      return null;
    }
  }

  // ============================================
  // GET DESCRIPTION (tries JSON first, then Section API)
  // ============================================
  
  async function getVariantDescription(variantId) {
    // Try JSON mode first
    if (CONFIG.useJsonMode && variantData) {
      const description = getDescriptionFromJson(variantId);
      if (description) {
        log('Got description from JSON');
        return description;
      }
    }
    
    // Fallback to Section API
    log('Falling back to Section API');
    return await fetchVariantDescriptionFromSection(variantId);
  }

  // ============================================
  // UPDATE THE DOM
  // ============================================
  
  function updateDescription(html) {
    if (!html) return;

    // Tabbed layout: the description is restructured into tabs inside
    // .product-description-full-width by product-description-tabs.liquid.
    // Replacing the generic container would drop the tabs, so instead hand the
    // fresh description back to the tab builder: reset the wrap to the raw
    // description (wrapped in .rte-page, which the builder looks for) and ask it
    // to rebuild.
    const tabWrap = document.querySelector('.product-description-full-width');
    if (tabWrap && tabWrap.querySelector('.dvl-tabs-nav, .dvl-tabs-placeholder')) {
      tabWrap.style.opacity = '0';
      tabWrap.style.transition = `opacity ${CONFIG.animationDuration}ms ease`;
      setTimeout(() => {
        tabWrap.innerHTML = '<div class="rte-page rte-page-fade">' + html + '</div>';
        tabWrap.style.opacity = '1';
        document.dispatchEvent(new CustomEvent('dvl:rebuild-tabs'));
        log('Description updated (tabbed) — rebuild requested');
      }, CONFIG.animationDuration);
      return;
    }

    const container = getDescriptionContainer();
    if (!container) {
      log('Description container not found');
      return;
    }

    // Fall back to data-default-description when section returns empty content
    const fallback = container.getAttribute('data-default-description') || defaultDescription || '';
    const content = (html && html.trim().length > 0) ? html : fallback;

    if (!content) return;

    // Fade out → swap → fade in
    container.style.transition = `opacity ${CONFIG.animationDuration}ms ease`;
    container.style.opacity = '0';

    setTimeout(() => {
      container.innerHTML = content;
      container.style.opacity = '1';
      log('Description updated');
    }, CONFIG.animationDuration);
  }

  // ============================================
  // EVENT HANDLER
  // ============================================
  
  let isUpdating = false;
  let pendingVariantId = null;

  async function handleVariantChange() {
    const variantId = getCurrentVariantId();
    if (!variantId) return;

    if (isUpdating) {
      pendingVariantId = variantId;
      return;
    }

    log('Variant changed to:', variantId);
    isUpdating = true;
    
    const description = await getVariantDescription(variantId);
    if (description) {
      updateDescription(description);
    }
    
    isUpdating = false;

    if (pendingVariantId && pendingVariantId !== variantId) {
      const pending = pendingVariantId;
      pendingVariantId = null;
      handleVariantChange(pending);
    }
  }

  // ============================================
  // INITIALIZE
  // ============================================
  
  async function init() {
    const productUrl = getProductUrl();
    if (!productUrl) {
      log('Not on a product page');
      return;
    }
    
    log('Initializing Variant Description Switcher');

    // Store the original description and populate from data-default-description if empty
    const container = getDescriptionContainer();
    if (container) {
      const dataDefault = container.getAttribute('data-default-description') || '';
      if (!container.innerHTML.trim() && dataDefault) {
        container.innerHTML = dataDefault;
        log('Populated from data-default-description on init');
      }
      defaultDescription = container.innerHTML;
    }

    // Try to load JSON data
    if (CONFIG.useJsonMode) {
      variantData = await loadVariantDescriptionsJson();
      if (variantData) {
        log('JSON mode active');
      } else {
        log('JSON mode unavailable, using Section API');
      }
    }

    // Attach event listeners
    document.addEventListener('change', (e) => {
      if (e.target.matches('select[name="id"], input[name="id"]')) {
        handleVariantChange();
        return;
      }
      if (e.target.matches('.single-option-selector, [data-single-option-selector], [data-option-selector]')) {
        setTimeout(handleVariantChange, 50);
      }
    });

    document.addEventListener('click', (e) => {
      const swatch = e.target.closest(
        '.swatch-element, .color-swatch, .variant-swatch, [data-swatch], ' +
        '.swatch label, .product-form__option-label, [data-variant-option]'
      );
      if (swatch) {
        setTimeout(handleVariantChange, 100);
      }
    });

    // URL change listeners
    let lastVariant = getCurrentVariantId();
    
    const handleUrlChange = () => {
      const currentVariant = getCurrentVariantId();
      if (currentVariant && currentVariant !== lastVariant) {
        lastVariant = currentVariant;
        handleVariantChange();
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function() {
      originalPushState.apply(this, arguments);
      setTimeout(handleUrlChange, 10);
    };
    
    history.replaceState = function() {
      originalReplaceState.apply(this, arguments);
      setTimeout(handleUrlChange, 10);
    };

    // Custom theme events
    ['variant:changed', 'variantChange', 'theme:variant:change'].forEach(eventName => {
      document.addEventListener(eventName, handleVariantChange);
    });

    log('Ready!');
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
